import api from "@/lib/api";
import type {
  StrapiResponse,
  Product,
  Category,
  ProductFilters,
  ApiRequestOptions,
} from "@/types";

export function mapCategory(raw: unknown): Category {
  if (!raw || typeof raw !== "object") {
    return {
      id: 0,
      documentId: "",
      name: "",
      slug: "",
      description: "",
      createdAt: "",
      updatedAt: "",
      publishedAt: "",
    };
  }

  const obj = raw as Record<string, unknown>;
  return {
    ...obj,
    id: typeof obj.id === "number" ? obj.id : 0,
    documentId: typeof obj.documentId === "string" ? obj.documentId : "",
    name: typeof obj.name === "string" ? obj.name : "",
    slug: typeof obj.slug === "string" ? obj.slug : "",
    description: typeof obj.description === "string" ? obj.description : "",
    createdAt: typeof obj.createdAt === "string" ? obj.createdAt : "",
    updatedAt: typeof obj.updatedAt === "string" ? obj.updatedAt : "",
    publishedAt: typeof obj.publishedAt === "string" ? obj.publishedAt : "",
  };
}

export function mapProduct(raw: unknown): Product {
  if (!raw || typeof raw !== "object") {
    return {
      id: 0,
      documentId: "",
      name: "",
      slug: "",
      description: [],
      price: 0,
      discount_price: 0,
      stock: 0,
      images: null,
      specifications: [],
      composition: "",
      measurement: "",
      brand: "",
      categories: [],
      createdAt: "",
      updatedAt: "",
      publishedAt: "",
    };
  }

  const obj = raw as Record<string, unknown>;
  const rawCategories = Array.isArray(obj.categories) ? obj.categories : [];
  const categories = rawCategories.map(mapCategory);

  return {
    ...obj,
    id: typeof obj.id === "number" ? obj.id : 0,
    documentId: typeof obj.documentId === "string" ? obj.documentId : "",
    name: typeof obj.name === "string" ? obj.name : "",
    slug: typeof obj.slug === "string" ? obj.slug : "",
    description: Array.isArray(obj.description)
      ? (obj.description as Product["description"])
      : [],
    price: typeof obj.price === "number" ? obj.price : 0,
    discount_price:
      typeof obj.discount_price === "number" ? obj.discount_price : 0,
    stock: typeof obj.stock === "number" ? obj.stock : 0,
    images: Array.isArray(obj.images)
      ? (obj.images as Product["images"])
      : null,
    specifications: Array.isArray(obj.specifications)
      ? (obj.specifications as Product["specifications"])
      : [],
    composition: typeof obj.composition === "string" ? obj.composition : "",
    measurement: typeof obj.measurement === "string" ? obj.measurement : "",
    brand: typeof obj.brand === "string" ? obj.brand : "",
    categories,
    rating: typeof obj.rating === "number" ? obj.rating : undefined,
    has_base_options: Boolean(obj.has_base_options),
    reinforced_base_price:
      typeof obj.reinforced_base_price === "number"
        ? obj.reinforced_base_price
        : undefined,
    reinforced_base_discount_price:
      typeof obj.reinforced_base_discount_price === "number"
        ? obj.reinforced_base_discount_price
        : undefined,
    reviewCount:
      typeof obj.reviewCount === "number" ? obj.reviewCount : undefined,
    createdAt: typeof obj.createdAt === "string" ? obj.createdAt : "",
    updatedAt: typeof obj.updatedAt === "string" ? obj.updatedAt : "",
    publishedAt: typeof obj.publishedAt === "string" ? obj.publishedAt : "",
  };
}

interface ProductApiParams extends ApiRequestOptions {
  populate: string[];
  filters?: {
    $or?: Array<{
      name?: {
        $containsi: string;
      };
      brand?: {
        $containsi: string;
      };
      composition?: {
        $containsi: string;
      };
      categories?: {
        name?: {
          $containsi: string;
        };
      };
    }>;
    categories?: {
      slug?: {
        $eq?: string;
        $in?: string[];
      };
    };
    price?: {
      $gte: number;
      $lte: number;
    };
    brand?: {
      $in: string[];
    };
    composition?: {
      $in: string[];
    };
    measurement?: {
      $in: string[];
    };
  };
  sort?: string;
}

export class ProductService {
  static async getProducts(
    filters?: ProductFilters,
  ): Promise<StrapiResponse<Product[]>> {
    try {
      const apiParams: ProductApiParams = {
        populate: ["categories", "images"],
      };

      if (filters) {
        if (!apiParams.filters) {
          apiParams.filters = {};
        }

        // Handle search query
        if (filters.search && filters.search.trim()) {
          const searchTerm = filters.search.trim();
          apiParams.filters.$or = [
            {
              name: {
                $containsi: searchTerm,
              },
            },
            {
              brand: {
                $containsi: searchTerm,
              },
            },
            {
              composition: {
                $containsi: searchTerm,
              },
            },
            {
              categories: {
                name: {
                  $containsi: searchTerm,
                },
              },
            },
          ];
        }

        if (filters.category) {
          apiParams.filters.categories = {
            slug: {
              $eq: filters.category,
            },
          };
        }

        if (filters.categories && filters.categories.length > 0) {
          apiParams.filters.categories = {
            slug: {
              $in: filters.categories,
            },
          };
        }

        if (filters.priceRange) {
          apiParams.filters.price = {
            $gte: filters.priceRange.min,
            $lte: filters.priceRange.max,
          };
        }

        if (filters.brands && filters.brands.length > 0) {
          apiParams.filters.brand = {
            $in: filters.brands,
          };
        }

        if (filters.compositions && filters.compositions.length > 0) {
          apiParams.filters.composition = {
            $in: filters.compositions,
          };
        }

        if (filters.measurements && filters.measurements.length > 0) {
          apiParams.filters.measurement = {
            $in: filters.measurements,
          };
        }

        if (filters.page) {
          apiParams.pagination = {
            page: filters.page,
            pageSize: filters.pageSize || 25,
          };
        }

        if (filters.sortBy) {
          switch (filters.sortBy) {
            case "price_asc":
              apiParams.sort = "price:asc";
              break;
            case "price_desc":
              apiParams.sort = "price:desc";
              break;
            case "name":
              apiParams.sort = "name:asc";
              break;
            case "rating":
              apiParams.sort = "rating:desc";
              break;
            case "newest":
              apiParams.sort = "createdAt:desc";
              break;
            default:
              break;
          }
        }
      }

      const response = await api.get<StrapiResponse<Product[]>>("/products", {
        params: apiParams,
      });
      return {
        ...response.data,
        data: Array.isArray(response.data.data)
          ? response.data.data.map(mapProduct)
          : [],
      };
    } catch (error) {
      console.error("Error fetching products:", error);
      throw error;
    }
  }

  static async getProduct(documentId: string): Promise<Product> {
    try {
      if (!documentId || typeof documentId !== "string") {
        throw new Error("Invalid documentId provided");
      }

      const response = await api.get<StrapiResponse<Product>>(
        `/products/${documentId}`,
        {
          params: {
            populate: ["categories", "images"] as const,
          } satisfies Pick<ProductApiParams, "populate">,
        },
      );
      return mapProduct(response.data.data);
    } catch (error) {
      console.error(`Error fetching product with ID ${documentId}:`, error);
      throw error;
    }
  }

  static async getProductBySlug(slug: string): Promise<Product | null> {
    try {
      if (!slug || typeof slug !== "string") {
        throw new Error("Invalid slug provided");
      }

      const response = await api.get<StrapiResponse<Product[]>>("/products", {
        params: {
          filters: {
            slug: {
              $eq: slug,
            },
          },
          populate: ["categories", "images"],
        },
      });

      const products = response.data.data || [];
      return products.length > 0 ? mapProduct(products[0]) : null;
    } catch (error) {
      console.error(`Error fetching product with slug ${slug}:`, error);
      throw error;
    }
  }

  static async getProductByDocumentId(
    documentId: string,
  ): Promise<Product | null> {
    try {
      if (!documentId || typeof documentId !== "string") {
        throw new Error("Invalid documentId provided");
      }

      const response = await api.get<StrapiResponse<Product[]>>("/products", {
        params: {
          filters: {
            documentId: {
              $eq: documentId,
            },
          },
          populate: ["categories", "images"],
        },
      });

      const products = response.data.data || [];
      return products.length > 0 ? mapProduct(products[0]) : null;
    } catch (error) {
      console.error(
        `Error fetching product with documentId ${documentId}:`,
        error,
      );
      throw error;
    }
  }

  static async createProduct(productData: Partial<Product>): Promise<Product> {
    try {
      if (!productData || typeof productData !== "object") {
        throw new Error("Invalid product data provided");
      }

      const response = await api.post<StrapiResponse<Product>>("/products", {
        data: productData,
      });
      return mapProduct(response.data.data);
    } catch (error) {
      console.error("Error creating product:", error);
      throw error;
    }
  }

  static async updateProduct(
    documentId: string,
    productData: Partial<Product>,
  ): Promise<Product> {
    try {
      if (!documentId || typeof documentId !== "string") {
        throw new Error("Invalid documentId provided");
      }

      if (!productData || typeof productData !== "object") {
        throw new Error("Invalid product data provided");
      }

      const response = await api.put<StrapiResponse<Product>>(
        `/products/${documentId}`,
        {
          data: productData,
        },
      );
      return mapProduct(response.data.data);
    } catch (error) {
      console.error(`Error updating product with ID ${documentId}:`, error);
      throw error;
    }
  }

  static async deleteProduct(documentId: string): Promise<void> {
    try {
      if (!documentId || typeof documentId !== "string") {
        throw new Error("Invalid documentId provided");
      }

      await api.delete(`/products/${documentId}`);
    } catch (error) {
      console.error(`Error deleting product with ID ${documentId}:`, error);
      throw error;
    }
  }

  static async getSimilarProducts(
    categorySlug: string | undefined,
    currentDocumentId: string,
    limit = 4,
  ): Promise<Product[]> {
    try {
      if (!currentDocumentId) return [];

      let similarProducts: Product[] = [];
      const populate = ["categories", "images"];

      if (categorySlug) {
        const categoryResponse = await api.get<StrapiResponse<Product[]>>(
          "/products",
          {
            params: {
              populate,
              filters: {
                categories: {
                  slug: {
                    $eq: categorySlug,
                  },
                },
                documentId: {
                  $ne: currentDocumentId,
                },
              },
              pagination: {
                pageSize: limit,
              },
            },
          },
        );
        similarProducts = (categoryResponse.data.data || []).map(mapProduct);
      }

      if (similarProducts.length < limit) {
        const remainingLimit = limit - similarProducts.length;
        const excludeIds = [
          currentDocumentId,
          ...similarProducts.map((p) => p.documentId),
        ];

        const fallbackResponse = await api.get<StrapiResponse<Product[]>>(
          "/products",
          {
            params: {
              populate,
              filters: {
                documentId: {
                  $notIn: excludeIds,
                },
              },
              pagination: {
                pageSize: remainingLimit,
              },
              sort: "createdAt:desc",
            },
          },
        );

        similarProducts = [
          ...similarProducts,
          ...(fallbackResponse.data.data || []).map(mapProduct),
        ];
      }

      return similarProducts;
    } catch (error) {
      console.error(
        "Error fetching similar products:",
        error instanceof Error ? error.message : String(error),
      );
      return [];
    }
  }
}
